import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

export type Lang = 'en' | 'zh'

const LS_KEY = 'lawyer-rag.lang.v2'
const DOC_LANG_KEY = 'lawyer-rag.doc-lang.v1'

/**
 * All user-facing strings.
 *
 * Convention: technical jargon stays English in both locales (e.g. RAG /
 * BM25 / Top-K / hybrid / dense / Mareva). Only natural-language prose
 * is translated. This matches how Malaysian legal practitioners
 * actually code-switch in practice.
 */
export interface Strings {
  // App chrome
  appName: string
  appNameLong: string
  newChat: string
  settings: string

  // Settings panel
  retrievalMode: string
  modeHybrid: string
  modeDense: string
  modeBm25: string
  topK: string
  temperature: string
  phraseMode: string
  phraseDescBefore: string
  phraseDescAfter: string

  // Category filter
  categoryFilter: string
  categoryFilterDesc: string
  categoryFilterAll: string
  /** Active-count summary, e.g. "3 of 8 selected" / "已选 3 / 8". */
  categoryFilterSummary: (selected: number, total: number) => string
  /** Per-preset labels, keyed by `categoryPresets.ts::CategoryPreset.key`. */
  categoryPreset: Record<string, string>

  // Sidebar footer
  historyTurns: (n: number) => string
  streaming: string

  // Composer
  composerPlaceholder: string
  composerPlaceholderStreaming: string
  enterHint: string
  multiTurnHint: string
  send: string
  stop: string
  sendTitle: string
  stopTitle: string

  // Attachment
  attachmentAttach: string
  attachmentAttachTitle: string
  attachmentRemove: string
  attachmentUploading: string
  /** Batch upload progress, e.g. "Uploading 2 of 5" / "上传中 2 / 5". */
  attachmentUploadingN: (current: number, total: number) => string
  attachmentFailed: string
  attachmentDropHere: string
  attachmentSupportedFormats: string
  attachmentPages: (n: number) => string
  attachmentChars: (n: number) => string
  attachmentTruncated: string
  /** Cap-reached hint, e.g. "Maximum 5 files reached". */
  attachmentMaxReached: (max: number) => string
  /** Short badge text on a chip whose upload was deduped by the server. */
  attachmentDuplicate: string
  /** Hover tooltip explaining the dedup badge. */
  attachmentDuplicateTitle: string
  /** Foot hint when 1+ attachments are active.  Receives the count and
   *  the first filename so it can pick a singular or summary phrasing. */
  attachmentActiveHint: (count: number, firstFilename: string) => string

  // Empty state
  emptyDescBefore: string
  emptyDescAfter: string
  examples: string[]

  // Errors
  errorLabel: string

  // Grounding verifier (post-generation hallucination check)
  /** "All N references grounded" / “已核实 N 处引用”. */
  verifierAllGrounded: (n: number) => string
  /** "K of N references not found in sources" / “N 处引用中 K 处未找到出处”. */
  verifierWarningTitle: (unverified: number, total: number) => string
  verifierWarningDesc: string
  /** Footer when the panel mixes verified + unverified, e.g.
   *  "K other references checked out OK". */
  verifierWarningFooter: (verifiedCount: number) => string
  /** Per-reference-kind label shown next to each flagged item. */
  verifierKindLabel: Record<string, string>

  // Assistant bubble
  generating: string
  empty: string
  thinking: string
  thoughtProcess: string
  sourcesN: (n: number) => string
  noSourcesWarning: string
  noSourcesDesc: string
  noSourcesAction: string
  copyAll: string
  copySuccess: string
  copyFailed: string
  favorite: string
  favorited: string
  favoriteFailed: string
  favoriteRemoveFailed: string
  unfavorited: string
  deleteTurn: string
  deleteTurnConfirm: string
  deleteTurnFailed: string
  saveToFolder: string
  folderLabel: string
  chooseFolder: string
  newFolder: string
  creating: string
  create: string
  saving: string
  save: string
  saved: string
  saveFailed: string
  folderCreated: string
  folderCreateFailed: string
  retrieveStat: string
  genStat: string
  totalStat: string
  tokensSuffix: string

  // Language toggle
  toggleLangTitle: string
}

const EN: Strings = {
  appName: 'Lawyer RAG',
  appNameLong: 'Lawyer RAG Assistant',
  newChat: 'New chat',
  settings: 'Settings',

  retrievalMode: 'Retrieval mode',
  modeHybrid: 'hybrid (RRF: dense + BM25)',
  modeDense: 'dense (BGE-M3)',
  modeBm25: 'bm25 (FTS)',
  topK: 'Top-K',
  temperature: 'Temperature',
  phraseMode: 'Phrase mode',
  phraseDescBefore: 'Use ',
  phraseDescAfter:
    ' so query and index match exactly. Best for case numbers and citations like [1992] 1 CLJ 14.',

  categoryFilter: 'Domain filter',
  categoryFilterDesc:
    'Restrict retrieval to selected domains. Empty = no filter (search the whole corpus).',
  categoryFilterAll: 'All domains',
  categoryFilterSummary: (selected, total) =>
    selected === 0 ? `${total} domains` : `${selected} of ${total} selected`,
  categoryPreset: {
    capital_markets: 'Capital markets',
    financial_regulation: 'Financial regulation',
    financial_disputes: 'Financial disputes',
    family: 'Family law',
    constitution: 'Constitution',
    criminal: 'Criminal & public',
    academic: 'Academic articles',
    misc_statutes: 'Other statutes',
  },

  historyTurns: (n) => `history: ${n} ${n === 1 ? 'turn' : 'turns'}`,
  streaming: 'streaming…',

  composerPlaceholder:
    "Ask a legal question — e.g. 'What is the burden of proof for fraud?'  (Shift+Enter for newline)",
  composerPlaceholderStreaming: 'Streaming… use Stop to cancel',
  enterHint: 'Enter to send · Shift+Enter for newline',
  multiTurnHint: 'Multi-turn is on. Settings on the left rail.',
  send: 'Send',
  stop: 'Stop',
  sendTitle: 'Send (Enter)',
  stopTitle: 'Stop generation',

  attachmentAttach: 'Attach',
  attachmentAttachTitle: 'Attach documents (PDF, DOCX, TXT, MD — up to 20 MB each, 5 total)',
  attachmentRemove: 'Remove attachment',
  attachmentUploading: 'Uploading',
  attachmentUploadingN: (current, total) => `Uploading ${current} of ${total}`,
  attachmentFailed: 'Upload failed',
  attachmentDropHere: 'Drop documents to attach',
  attachmentSupportedFormats: 'PDF · DOCX · TXT · MD',
  attachmentPages: (n) => `${n} ${n === 1 ? 'page' : 'pages'}`,
  attachmentChars: (n) => `${n.toLocaleString()} chars`,
  attachmentTruncated: 'truncated',
  attachmentMaxReached: (max) => `Maximum ${max} files reached`,
  attachmentDuplicate: 'Already uploaded',
  attachmentDuplicateTitle:
    'This file is byte-for-byte identical to one already attached — '
    + 'the server reused the existing upload rather than storing a copy.',
  attachmentActiveHint: (count, firstFilename) =>
    count === 1
      ? `Analysing against “${firstFilename}”. Follow-ups stay attached until you remove it.`
      : `Analysing against ${count} documents. Follow-ups stay attached until you remove them.`,

  emptyDescBefore:
    'Ask any question about your legal corpus — Malaysian case law, local precedents, or your case files. Answers cite numbered sources from the local DB. Multi-turn is on; use ',
  emptyDescAfter: ' to start fresh.',
  examples: [
    'What is the burden of proof for fraud in Malaysian civil cases?',
    'Summarise the conflict between Saminathan v Pappa and Bater v Bater.',
    'When does a Mareva injunction lie under Order 29?',
  ],

  errorLabel: 'Error',

  verifierAllGrounded: (n) =>
    n === 1
      ? '1 reference checked against sources'
      : `${n} references checked against sources`,
  verifierWarningTitle: (unverified, total) =>
    `${unverified} of ${total} references not found in retrieved sources`,
  verifierWarningDesc:
    'These legal references appear in the answer but do not match any of '
    + 'the retrieved chunks. The model may have produced them from prior '
    + 'training rather than the corpus — verify before relying on them.',
  verifierWarningFooter: (n) =>
    n === 1
      ? '1 other reference was matched against the sources.'
      : `${n} other references were matched against the sources.`,
  verifierKindLabel: {
    section:   'Section',
    article:   'Article',
    case_name: 'Case',
    citation:  'Citation',
    act:       'Act',
  },

  generating: 'generating…',
  empty: '(empty)',
  thinking: 'Thinking…',
  thoughtProcess: 'Thought process',
  sourcesN: (n) => `${n} source${n === 1 ? '' : 's'}`,
  noSourcesWarning: 'No relevant precedents found',
  noSourcesDesc: 'The knowledge base does not contain cases relevant to this matter. The analysis is based on the uploaded case files and general legal knowledge — please verify independently.',
  noSourcesAction: 'Go to Knowledge Base',
  copyAll: 'Copy answer',
  copySuccess: 'Copied to clipboard',
  copyFailed: 'Copy failed',
  favorite: 'Favorite',
  favorited: 'Favorited',
  favoriteFailed: 'Unable to favorite',
  favoriteRemoveFailed: 'Unable to remove favorite',
  unfavorited: 'Removed from favorites',
  deleteTurn: 'Remove message',
  deleteTurnConfirm: 'Delete this message from the conversation?',
  deleteTurnFailed: 'Unable to remove the message',
  saveToFolder: 'Save',
  folderLabel: 'Folder',
  chooseFolder: 'Choose folder',
  newFolder: 'New folder',
  creating: 'Creating',
  create: 'Create',
  saving: 'Saving',
  save: 'Save snippet',
  saved: 'Saved',
  saveFailed: 'Save failed',
  folderCreated: 'Folder created',
  folderCreateFailed: 'Could not create folder',
  retrieveStat: 'retrieve',
  genStat: 'gen',
  totalStat: 'total',
  tokensSuffix: 'tokens',

  toggleLangTitle: '切换到中文',
}

const ZH: Strings = {
  appName: '律师 RAG',
  appNameLong: '律师 RAG 助手',
  newChat: '新对话',
  settings: '设置',

  retrievalMode: '检索模式',
  modeHybrid: 'hybrid · 混合 (RRF: dense + BM25)',
  modeDense: 'dense · 稠密向量 (BGE-M3)',
  modeBm25: 'bm25 · 全文检索 (FTS)',
  topK: 'Top-K',
  temperature: 'Temperature · 温度',
  phraseMode: 'Phrase · 短语模式',
  phraseDescBefore: '使用 ',
  phraseDescAfter:
    '，让查询语和索引精确匹配。适合处理案号和引证号，例如 [1992] 1 CLJ 14。',

  categoryFilter: '领域筛选',
  categoryFilterDesc:
    '将检索限定在所选领域内。不勾选 = 不过滤（全语料库检索）。',
  categoryFilterAll: '全部领域',
  categoryFilterSummary: (selected, total) =>
    selected === 0 ? `共 ${total} 类` : `已选 ${selected} / ${total}`,
  categoryPreset: {
    capital_markets: '资本市场',
    financial_regulation: '金融监管',
    financial_disputes: '金融纠纷',
    family: '婚姻家事',
    constitution: '宪法 · 基本法',
    criminal: '刑事 · 公法',
    academic: '学术文章',
    misc_statutes: '其他法令',
  },

  historyTurns: (n) => `历史: ${n} 轮`,
  streaming: '生成中…',

  composerPlaceholder:
    '向法律语料库提问 —— 比如 “民事案件中欺诈的举证责任是什么？”（Shift+Enter 换行）',
  composerPlaceholderStreaming: '生成中… 点 Stop 取消',
  enterHint: 'Enter 发送 · Shift+Enter 换行',
  multiTurnHint: '多轮对话已开启。所有设置在左侧栏。',
  send: '发送',
  stop: '停止',
  sendTitle: '发送 (Enter)',
  stopTitle: '停止生成',

  attachmentAttach: '上传',
  attachmentAttachTitle: '上传文档进行分析（PDF / DOCX / TXT / MD，单件上限 20 MB，最多 5 个）',
  attachmentRemove: '移除附件',
  attachmentUploading: '上传中',
  attachmentUploadingN: (current, total) => `上传中 ${current} / ${total}`,
  attachmentFailed: '上传失败',
  attachmentDropHere: '拖放文档到此处以上传',
  attachmentSupportedFormats: 'PDF · DOCX · TXT · MD',
  attachmentPages: (n) => `${n} 页`,
  attachmentChars: (n) => `${n.toLocaleString()} 字`,
  attachmentTruncated: '已截断',
  attachmentMaxReached: (max) => `附件数已达上限 ${max} 个`,
  attachmentDuplicate: '已上传过',
  attachmentDuplicateTitle:
    '该文件字节上与之前已附加的文件完全一致，服务器已复用原有上传，未重复存储。',
  attachmentActiveHint: (count, firstFilename) =>
    count === 1
      ? `正在结合「${firstFilename}」进行分析。后续问题仍会带上附件，点 × 可移除。`
      : `正在结合 ${count} 份附件进行分析。后续问题仍会带上附件，点各附件的 × 可移除。`,

  emptyDescBefore:
    '可以向你的法律语料库提任何问题 —— 马来西亚判例、本地先例、案件卷宗都可以。每条回答会引用本地数据库中编号的来源。多轮对话已开启；点 ',
  emptyDescAfter: ' 重新开始。',
  examples: [
    '马来西亚民事案件中欺诈的举证责任标准是什么？',
    '比较 Saminathan v Pappa 与 Bater v Bater 在举证责任上的判决冲突。',
    'Order 29 规则下 Mareva 禁令的适用条件是什么？',
  ],

  errorLabel: '错误',

  verifierAllGrounded: (n) => `已核实 ${n} 处引用`,
  verifierWarningTitle: (unverified, total) =>
    `${total} 处引用中，${unverified} 处未在检索到的来源中找到`,
  verifierWarningDesc:
    '以下法律引用出现在答案中，但与本次检索到的任何片段都对不上。模型'
    + '可能是凭训练记忆生成，而非从语料库引用——使用前请亲自核对。',
  verifierWarningFooter: (n) => `另外 ${n} 处引用已核对成功。`,
  verifierKindLabel: {
    section:   '法条',
    article:   '条文',
    case_name: '案例',
    citation:  '引证',
    act:       '法令',
  },

  generating: '生成中…',
  empty: '(空)',
  thinking: '正在思考…',
  thoughtProcess: '已完成思考',
  sourcesN: (n) => `${n} 条来源`,
  noSourcesWarning: '未找到相关判例',
  noSourcesDesc: '知识库中没有与本案件相关的判例。以上分析完全基于上传的案件材料和通用法律知识，请自行核实。',
  noSourcesAction: '前往知识库补充判例',
  copyAll: '复制回答',
  copySuccess: '已复制到剪贴板',
  copyFailed: '复制失败',
  favorite: '收藏',
  favorited: '已收藏',
  favoriteFailed: '收藏失败',
  favoriteRemoveFailed: '取消收藏失败',
  unfavorited: '已取消收藏',
  deleteTurn: '删除消息',
  deleteTurnConfirm: '确定要从对话中删除这条消息吗？',
  deleteTurnFailed: '无法删除消息',
  saveToFolder: '保存',
  folderLabel: '文件夹',
  chooseFolder: '选择文件夹',
  newFolder: '新建文件夹',
  creating: '创建中',
  create: '创建',
  saving: '保存中',
  save: '保存片段',
  saved: '已保存',
  saveFailed: '保存失败',
  folderCreated: '已创建文件夹',
  folderCreateFailed: '文件夹创建失败',
  retrieveStat: '检索',
  genStat: '生成',
  totalStat: '总计',
  tokensSuffix: 'tokens',

  toggleLangTitle: 'Switch to English',
}

const DICT: Record<Lang, Strings> = { en: EN, zh: ZH }

interface Ctx {
  lang: Lang
  setLang: (l: Lang) => void
  docLang: Lang
  setDocLang: (l: Lang) => void
  s: Strings
}

const LanguageContext = createContext<Ctx | null>(null)

function detectInitialLang(): Lang {
  if (typeof window === 'undefined') return 'en'
  try {
    const saved = window.localStorage.getItem(LS_KEY)
    if (saved === 'en' || saved === 'zh') return saved
  } catch {
    /* localStorage may be disabled — fall through to navigator detection */
  }
  const nav = window.navigator.language?.toLowerCase() ?? ''
  return nav.startsWith('zh') ? 'zh' : 'en'
}

function detectInitialDocLang(): Lang {
  if (typeof window === 'undefined') return 'en'
  try {
    const saved = window.localStorage.getItem(DOC_LANG_KEY)
    if (saved === 'en' || saved === 'zh') return saved
  } catch {
    /* ignore */
  }
  return 'en'
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectInitialLang)
  const [docLang, setDocLangState] = useState<Lang>(detectInitialDocLang)

  const setLang = (l: Lang) => {
    setLangState(l)
    try {
      window.localStorage.setItem(LS_KEY, l)
    } catch {
      /* ignore */
    }
  }

  const setDocLang = (l: Lang) => {
    setDocLangState(l)
    try {
      window.localStorage.setItem(DOC_LANG_KEY, l)
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : 'en'
  }, [lang])

  const value = useMemo<Ctx>(
    () => ({ lang, setLang, docLang, setDocLang, s: DICT[lang] }),
    [lang, docLang],
  )

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useI18n(): Ctx {
  const ctx = useContext(LanguageContext)
  if (!ctx) {
    throw new Error('useI18n must be used inside <LanguageProvider>')
  }
  return ctx
}
